// shared-lib/group-customization/mountRules.js
//
// Which objects sit flush against a wall. Moved here from the web editor's
// `groupCustomization.js` so the build-time link emitters can state wall
// mounting as an `on-wall` link instead of leaving it to a type-inferred
// projection inside the solver (reference-docs/layout-relationships §2.3).
//
// Accepts both shapes: a world-space room object (fields on the object) and
// an editor child (`original` carries the room object).

// Types that always sit flush with the parent wall plane.
const ALWAYS_WALL_MOUNTED = new Set([
  'mirror',
  'wall-light',
  'overhead-wall-light',
  'wall-light-fixture',
]);

const field = (c, key) => c?.original?.[key] ?? c?.[key];

const isWallMountedSink = (c) =>
  c?.type === 'sink' && field(c, 'sinkType') === 'wall-mount';

const isWallMountedFaucet = (c) => {
  if (c?.type !== 'bathroom-sink-faucet' && c?.type !== 'bathroom-tub-faucet')
    return false;
  const mount = field(c, 'mountType');
  return mount === 'wall' || mount === 'wall-mount';
};

function isWallMounted(c) {
  return (
    ALWAYS_WALL_MOUNTED.has(c?.type) ||
    isWallMountedSink(c) ||
    isWallMountedFaucet(c)
  );
}

/**
 * The wall a mounted object hangs on, or null. `parentWallIds[0]` is the
 * builders' stamp; a `parents` entry of type wall is the older form.
 */
function mountedWallId(obj) {
  const stamped = field(obj, 'parentWallIds');
  if (Array.isArray(stamped) && stamped[0]) return stamped[0];
  const parents = field(obj, 'parents');
  const wallParent = Array.isArray(parents)
    ? parents.find((p) => /wall/i.test(p?.type || ''))
    : null;
  return wallParent?.id || null;
}

/**
 * dz (anchor-local) at which a child of `childDepth` sits with its back face
 * flush with `wall`'s room-side line. Projects the wall midpoint onto the
 * anchor's local z axis — exact when the anchor is parallel to the wall,
 * which is every case a fixture group covers. One rule for the editor's
 * type-inferred wall snap and for `on-wall` links.
 */
function wallFaceDzInAnchorFrame(wall, anchorObject, childDepth) {
  const bl = wall?.alignment?.bottomLeft;
  const br = wall?.alignment?.bottomRight;
  if (!bl || !br || !anchorObject) return null;
  const wallMidX = (bl.x + br.x) / 2;
  const wallMidZ = (bl.z + br.z) / 2;
  const rotY = anchorObject.rotation?.y || 0;
  const sin = Math.sin(rotY);
  const cos = Math.cos(rotY);
  const diffX = wallMidX - (anchorObject.center?.x ?? 0);
  const diffZ = wallMidZ - (anchorObject.center?.z ?? 0);
  const wallPlaneLocalZ = diffX * sin + diffZ * cos;
  // child.dz - childDepth/2 = wallPlaneLocalZ
  return wallPlaneLocalZ + (childDepth || 0) / 2;
}

module.exports = {
  ALWAYS_WALL_MOUNTED,
  isWallMounted,
  mountedWallId,
  wallFaceDzInAnchorFrame,
};
